import { memo, useRef, useState, useEffect, useContext, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import ComponentActionsGenerator from "../ComponentActionsGenerator";
import "./css/index.css";
import CustomDrawerPanel from "layouts/Flow Designer/components/CustomDrawerPanel";
import SoftBox from "components/SoftBox";
import { COMMON_COMPONENT_TYPES } from "constants/flowDesigner";
import { TabContextApi } from "../../context";
import NodePanelTabs from "./components/NodePanelTabs";
import Comments from "layouts/Flow Designer/components/FlowPropertiesPanel/components/Comments";
import ComponentVersions from "./components/ComponentVersions";
import PropertiesTab from "../FlowPropertiesPanel/components/Properties";
import { SET_TO_OPEN_NODE_TAB } from "redux/constants/flowDesigner";
import DiscardDialog from "cxfcomponents/DiscardDialog";
import { hideComponentPropsPanel, setComponentPropsPanelFormIsDirty } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { Actions } from "flexlayout-react";

const TabPanel = ({ children, value = 0, index = 0 }) => (
   <div
      style={{
         display: value === index ? "flex" : "none",
         flexDirection: "column",
         height: "100%",
         minHeight: 0
      }}
   >
      {children}
   </div>
);

const ComponentPropertiesPanel = ({ tabId, node }) => {
   const tabs = useSelector((store) => store.designer.componentPropsPanel.tabs);
   const tabState = tabs[tabId] || {};
   const { selectedNode, showComponentPropsPanel, previousSelectedNodeId, flowElement, isDirty: reduxIsDirty } = tabState;
   const dispatch = useDispatch();
   const [tabValue, setTabValue] = useState(0);
   const { autoSaveFlow } = useContext(TabContextApi);
   const { model, registerTabCloseGuard, unregisterTabCloseGuard } = useFlexLayout();
   const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
   const [actionsResetKey, setActionsResetKey] = useState(0);
   const isConnector = selectedNode?.connectorId;
   const pendingActionRef = useRef(null);
   const isDirtyRef = useRef(false);
   const actionsGeneratorRef = useRef(null);
   const propertiesRef = useRef(null);
   const commentsRef = useRef(null);

   const handleClosePanel = useCallback(() => {
      dispatch(hideComponentPropsPanel({ tabId }));
      model.doAction(Actions.deleteTab(tabId, { source: "confirmed" }));
   }, [dispatch, tabId, model]);

   const handleDirtyChange = useCallback(
      (value) => {
         if (value === "cancel") {
            if (isDirtyRef.current) {
               pendingActionRef.current = { action: "closePanel" };
               setOpenDiscardDialog(true);
            } else {
               handleClosePanel();
            }
            return;
         }
         isDirtyRef.current = Boolean(value);
         dispatch(setComponentPropsPanelFormIsDirty(tabId, Boolean(value)));
      },
      [handleClosePanel, dispatch, tabId]
   );

   const handleChangeTab = useCallback(
      (event, newValue) => {
         if (isDirtyRef.current && newValue !== tabValue) {
            pendingActionRef.current = { action: "switchTab", index: newValue };
            setOpenDiscardDialog(true);
         } else {
            setTabValue(newValue);
         }
      },
      [tabValue]
   );

   const handleDiscardAccept = useCallback(() => {
      setOpenDiscardDialog(false);
      isDirtyRef.current = false;
      dispatch(setComponentPropsPanelFormIsDirty(tabId, false));

      switch (tabValue) {
         case 0:
            actionsGeneratorRef.current?.reset?.();
            setActionsResetKey((k) => k + 1);
            break;
         case 1:
            propertiesRef.current?.reset?.();
            break;
         case 2:
            commentsRef.current?.reset?.();
            break;
      }

      switch (pendingActionRef.current?.action) {
         case "switchTab":
            setTabValue(pendingActionRef.current?.index);
            break;
         case "closePanel":
            handleClosePanel();
            break;
      }

      pendingActionRef.current = null;
   }, [handleClosePanel, dispatch, tabId, tabValue]);

   const handleDiscardClose = useCallback(() => {
      setOpenDiscardDialog(false);
      pendingActionRef.current = null;
   }, []);

   useEffect(() => {
      if (!showComponentPropsPanel) setTabValue(0);
   }, [showComponentPropsPanel]);

   useEffect(() => {
      if (selectedNode?.key === COMMON_COMPONENT_TYPES.END) setTabValue(1);
   }, [selectedNode?.key]);

   useEffect(() => {
      if (node.visible) {
         dispatch({
            type: SET_TO_OPEN_NODE_TAB,
            payload: node.attributes.config.nodeId
         });
      }
   }, []);

   useEffect(() => {
      isDirtyRef.current = false;
      dispatch(setComponentPropsPanelFormIsDirty(tabId, false));
   }, [selectedNode?.elementId]);

   useEffect(() => {
      registerTabCloseGuard(tabId, () => {
         if (!isDirtyRef.current) {
            handleClosePanel();
            return false;
         }
         pendingActionRef.current = { action: "closePanel" };
         setOpenDiscardDialog(true);
         return false;
      });
      return () => unregisterTabCloseGuard(tabId);
   }, [tabId, handleClosePanel, registerTabCloseGuard, unregisterTabCloseGuard]);

   useEffect(() => {
      const handleBeforeUnload = (event) => {
         if (reduxIsDirty && !document.getElementById("idleDialogDescription")) {
            event.preventDefault();
            event.returnValue = "You have unsaved changes. Are you sure you want to leave?";
         }
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
   }, [reduxIsDirty]);

   return (
      <>
         <CustomDrawerPanel open={true} className="no-translate node-props-panel">
            <SoftBox>
               <NodePanelTabs tabValue={tabValue} onChange={handleChangeTab} isConnector={isConnector} />
            </SoftBox>

            <TabPanel value={tabValue} index={0}>
               <ComponentActionsGenerator key={isConnector ? `${actionsResetKey}-${selectedNode?.version}-${selectedNode?.data?.connectorVersionUpdatedDatetime || ""}` : actionsResetKey} ref={actionsGeneratorRef} tabId={tabId} tabState={tabState} onDirtyChange={handleDirtyChange} />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
               <PropertiesTab ref={propertiesRef} selectedNode={selectedNode} flowElement={flowElement} tabId={tabId} onDirtyChange={handleDirtyChange} />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
               <Comments ref={commentsRef} node_id={selectedNode?.elementId} onDirtyChange={handleDirtyChange} />
            </TabPanel>

            {isConnector && (
               <TabPanel value={tabValue} index={3}>
                  <ComponentVersions selectedNode={selectedNode} previousSelectedNodeId={previousSelectedNodeId} autoSaveFlow={autoSaveFlow} />
               </TabPanel>
            )}
         </CustomDrawerPanel>

         <DiscardDialog open={openDiscardDialog} handleDialogClose={handleDiscardClose} onAccept={handleDiscardAccept} />
      </>
   );
};

export default memo(ComponentPropertiesPanel);
